import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Check, Pencil, Upload } from "lucide-react";

export default function HouseEditor({ svjId, onChange }) {
  const [svj, setSvj] = useState(null);
  const [newEnt, setNewEnt] = useState("");
  const load = useCallback(() => api.get(`/svjs/${svjId}`).then((r) => setSvj(r.data)).catch((e) => toast.error(apiErr(e))), [svjId]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); await load(); onChange?.(); } catch (e) { toast.error(apiErr(e)); }
  };
  const addEntrance = () => {
    if (!newEnt.trim()) return;
    run(() => api.post(`/svjs/${svjId}/entrances`, { label: newEnt.trim(), floors: [] }), "Vchod přidán").then(() => setNewEnt(""));
  };

  const fileRef = useRef(null);
  const importOwners = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/svjs/${svjId}/import-owners`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Jména doplněna u ${data.updated} z ${data.total} bytů${data.missing.length ? ` · nenalezeno: ${data.missing.slice(0, 5).join(", ")}${data.missing.length > 5 ? "…" : ""}` : ""}`);
      await load(); onChange?.();
    } catch (e) { toast.error(apiErr(e)); } finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  if (!svj) return <p className="text-slate-500 mt-4">Načítání…</p>;
  const total = svj.entrances.reduce((s, e) => s + e.floors.reduce((x, f) => x + f.apartments.length, 0), 0);

  return (
    <div className="mt-4 space-y-4" data-testid="house-editor">
      <div className="bg-white rounded-xl border p-4 flex flex-wrap items-end gap-3">
        <div className="text-sm text-slate-600 flex-1"><b>{svj.entrances.length}</b> vchodů · <b>{total}</b> bytů. Změny se ukládají okamžitě. Byty s aktivní rezervací nelze odebrat.</div>
        <input ref={fileRef} type="file" accept=".pdf,.csv,.xlsx,.xls" className="hidden" onChange={importOwners} data-testid="import-owners-file" />
        <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} title="Nahrajte seznam jednotek (PDF/CSV) – doplní jména vlastníků k existujícím bytům podle vchodu a čísla" data-testid="import-owners-btn"><Upload className="h-4 w-4 mr-1" /> Importovat jména vlastníků</Button>
        <div className="space-y-1"><Label className="text-xs">Nový vchod</Label><Input value={newEnt} onChange={(e) => setNewEnt(e.target.value)} placeholder="např. 2 nebo B" className="w-32 h-9" data-testid="new-entrance-label" /></div>
        <Button size="sm" onClick={addEntrance} disabled={!newEnt.trim()} style={{ background: "#0A2540" }} data-testid="add-entrance-btn"><Plus className="h-4 w-4 mr-1" /> Přidat vchod</Button>
      </div>
      {svj.entrances.map((e) => <EntranceCard key={e.id} ent={e} run={run} />)}
      {svj.entrances.length === 0 && <div className="bg-white rounded-xl border p-8 text-center text-slate-500">Zatím žádný vchod.</div>}
    </div>
  );
}

function InlineEdit({ value, onSave, testId, className = "", placeholder }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  if (!edit) return (
    <button onClick={() => setEdit(true)} className={`inline-flex items-center gap-1 hover:text-[#C5A059] ${className} ${!value && placeholder ? "italic opacity-60" : ""}`} data-testid={`${testId}-edit`} title="Upravit">
      {value || placeholder} <Pencil className="h-3 w-3 opacity-50" />
    </button>
  );
  const save = () => { setEdit(false); if (v.trim() !== value && (v.trim() || placeholder)) onSave(v.trim()); };
  return (
    <span className="inline-flex items-center gap-1">
      <Input value={v} onChange={(ev) => setV(ev.target.value)} onKeyDown={(ev) => ev.key === "Enter" && save()} className={`h-7 text-sm ${placeholder ? "w-44" : "w-24"}`} placeholder={placeholder} autoFocus data-testid={`${testId}-input`} />
      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={save} data-testid={`${testId}-save`}><Check className="h-4 w-4" /></Button>
    </span>
  );
}

function EntranceCard({ ent, run }) {
  const [fl, setFl] = useState({ label: "", apts: "" });
  const addFloor = () => {
    const apartments = fl.apts.split(/[,\s;]+/).map((s) => s.trim()).filter(Boolean);
    run(() => api.post(`/entrances/${ent.id}/floors`, { label: fl.label, apartments }), "Patro přidáno").then(() => setFl({ label: "", apts: "" }));
  };
  const delEnt = () => window.confirm(`Smazat vchod ${ent.label} včetně všech bytů?`) && run(() => api.delete(`/entrances/${ent.id}`), "Vchod smazán");
  return (
    <div className="bg-white rounded-xl border p-5 space-y-3" data-testid={`he-entrance-${ent.id}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-[#0A2540] flex items-center gap-1">Vchod <InlineEdit value={ent.label} testId={`he-ent-${ent.id}`} onSave={(label) => run(() => api.patch(`/entrances/${ent.id}`, { label }), "Vchod přejmenován")} /></h3>
        <Button size="sm" variant="ghost" className="text-red-500" onClick={delEnt} data-testid={`he-del-ent-${ent.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      {ent.floors.map((f) => <FloorRow key={f.label} ent={ent} floor={f} run={run} />)}
      <div className="bg-slate-50 rounded-lg p-3 flex flex-wrap items-end gap-2">
        <div className="space-y-1"><Label className="text-xs">Nové patro</Label><Input value={fl.label} onChange={(e) => setFl({ ...fl, label: e.target.value })} placeholder="např. 4 nebo Přízemí" className="h-8 w-36" data-testid={`he-new-floor-${ent.id}`} /></div>
        <div className="space-y-1 flex-1 min-w-48"><Label className="text-xs">Čísla bytů (oddělená čárkou)</Label><Input value={fl.apts} onChange={(e) => setFl({ ...fl, apts: e.target.value })} placeholder="13, 14, 15" className="h-8" data-testid={`he-new-floor-apts-${ent.id}`} /></div>
        <Button size="sm" variant="outline" onClick={addFloor} disabled={!fl.label.trim()} data-testid={`he-add-floor-${ent.id}`}><Plus className="h-4 w-4 mr-1" /> Přidat patro</Button>
      </div>
    </div>
  );
}

function FloorRow({ ent, floor, run }) {
  const [num, setNum] = useState("");
  const addApt = () => run(() => api.post(`/entrances/${ent.id}/apartments`, { floor_label: floor.label, number: num }), "Byt přidán").then(() => setNum(""));
  const delFloor = () => window.confirm(`Smazat patro „${floor.label}“ (${floor.apartments.length} bytů)?`) &&
    run(() => api.delete(`/entrances/${ent.id}/floors/${encodeURIComponent(floor.label)}`), "Patro smazáno");
  return (
    <div className="flex items-start gap-3 border rounded-lg p-2" data-testid={`he-floor-${ent.id}-${floor.label}`}>
      <div className="w-32 shrink-0 text-sm pt-1">
        <span className="text-[10px] text-slate-400 block">Patro</span>
        <InlineEdit value={floor.label} testId={`he-floor-${ent.id}-${floor.label}`} className="font-medium" onSave={(label) => run(() => api.patch(`/entrances/${ent.id}/floors/${encodeURIComponent(floor.label)}`, { label }), "Patro přejmenováno")} />
      </div>
      <div className="flex-1 flex flex-wrap gap-1.5 items-center">
        {floor.apartments.map((a) => (
          <span key={a.id} className="inline-flex items-center gap-1 border rounded-md px-2 py-1 text-sm bg-white" data-testid={`he-apt-${a.id}`}>
            <InlineEdit value={a.number} testId={`he-apt-${a.id}`} onSave={(number) => run(() => api.patch(`/apartments/${a.id}`, { number }), "Byt přejmenován")} />
            <InlineEdit value={a.owner_name || ""} placeholder="jméno" testId={`he-apt-owner-${a.id}`} className="text-xs text-slate-500" onSave={(owner_name) => run(() => api.patch(`/apartments/${a.id}`, { owner_name }), "Jméno uloženo")} />
            <button onClick={() => run(() => api.delete(`/apartments/${a.id}`), "Byt odebrán")} className="text-slate-300 hover:text-red-500" title="Odebrat byt" data-testid={`he-del-apt-${a.id}`}>×</button>
          </span>
        ))}
        <span className="inline-flex items-center gap-1">
          <Input value={num} onChange={(e) => setNum(e.target.value)} onKeyDown={(e) => e.key === "Enter" && num.trim() && addApt()} placeholder="č." className="h-8 w-16 text-center" data-testid={`he-new-apt-${ent.id}-${floor.label}`} />
          <Button size="sm" variant="ghost" className="h-8 text-[#0A2540]" disabled={!num.trim()} onClick={addApt} data-testid={`he-add-apt-${ent.id}-${floor.label}`}><Plus className="h-3 w-3" /> byt</Button>
        </span>
      </div>
      <Button size="sm" variant="ghost" className="text-red-400" onClick={delFloor} data-testid={`he-del-floor-${ent.id}-${floor.label}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
  );
}
